import { AO_URL, CIRCULAR_URL, PhSection } from '../components/dossier/PhFindingSection'

const CODEX_URL = 'https://www.fao.org/ag/humannutrition/32444-09f5545b8abe9a0c3baf01a4502ac36e4.pdf'
const BOOKLET_URL =
  'https://www.fda.gov.ph/draft-for-comments-revised-rules-and-regulations-on-the-licensing-of-food-establishments-and-registration-of-processed-food-and-other-food-products-and-for-other-purposes-repealing-administrative/'
const BC_URL = 'https://www.fda.gov.ph/wp-content/uploads/2021/05/Bureau-Circular-No.-2006-016.pdf'
const RA_8976_URL = 'https://elibrary.judiciary.gov.ph/thebookshelf/showdocs/2/5607'

const ao = (clause?: string) => ({ text: clause ? `AO 2014-0030 ${clause}` : 'AO 2014-0030', url: AO_URL })
const circular = (clause: string) => ({ text: `FDA Circular 2023-009 ${clause}`, url: CIRCULAR_URL })
const booklet = (_page?: string) => ({ text: 'FDA AO - 2014-0029', url: BOOKLET_URL })
const codex = (clause?: string) => ({ text: clause ? `Codex CAC/GL 23-1997 ${clause}` : 'Codex CAC/GL 23-1997', url: CODEX_URL })

export const nutritionPanelSection: PhSection = {
  title: 'Nutrition Panel',
  checks: '15 checks',
  chips: [],
  findings: [
    {
      name: '% PDRI declaration',
      status: 'Fail',
      issue:
        'Nutrition panel does not show %PDRI values — required for all prepackaged processed food, per FDA Circular 2023-009.',
      recommendation: 'Add a %PDRI column based on the PDRI 2015 reference for male adults 19–29.',
      citation: circular('§IV.A'),
    },
    {
      name: 'Energy unit declaration',
      status: 'Fail',
      issue: 'Energy is declared only in kJ (366kJ/serve, 1210kJ/100g); no kcal shown.',
      recommendation: 'Add Calories (kcal) declaration (1 kcal ≈ 4.184 kJ).',
      citation: ao('§VI.A.11.c.1'),
    },
    {
      name: 'Footnote semantic coherence',
      status: 'Fail',
      issue:
        "Two footnote symbols in use: asterisk covers '27% MORE PROTEIN*'/'57% LESS FAT*' with a back-panel explanation; the Nutrition table itself uses a caret on 'Protein^', matching '8g PROTEIN PER SERVE^', with no explanatory footnote anywhere.",
      recommendation: 'Add a footnote explaining the caret (^) symbol.',
      citation: ao('§V.D'),
    },
    {
      name: 'Mandatory nutrient presence',
      status: 'Fail',
      issue: 'Trans Fat, Cholesterol, and Dietary Fiber are missing. (Saturated Fat and Sodium are already present.)',
      recommendation: 'Add Trans Fat, Cholesterol, and Dietary Fiber declarations.',
      citation: ao('§VI.A.11.a'),
    },
    {
      name: 'Nutrient rounding rules',
      status: 'Fail',
      issue: 'Fat, total is declared as 3.7g/serve (12.4g/100g), not rounded to the nearest gram.',
      recommendation: 'Round to 4g per serve.',
      citation: ao('§VI.A.11.c.1'),
    },
    {
      name: 'Presence + format — Energy',
      status: 'Fail',
      issue: 'Same as the Energy unit finding — kJ only, no kcal, no %PDRI.',
      recommendation: 'See Energy unit declaration finding.',
      citation: ao('§VI.A.11.c.1'),
    },
    {
      name: 'Presence + format — Fat, total',
      status: 'Fail',
      issue: "Nutrient name 'Fat, total' deviates from the standard 'Total Fat (g)'; %PDRI missing. (Actual value 3.7g/12.4g, unit shown correctly.)",
      recommendation: "Rename to 'Total Fat (g)' and add %PDRI.",
      citation: ao('§VI.A.11.a'),
    },
    {
      name: 'Presence + format — Carbohydrate',
      status: 'Fail',
      issue: "Nutrient name 'Carbohydrate' deviates from the standard 'Total Carbohydrates (g)'; %PDRI missing. (Actual value 5.2g/17.4g.)",
      recommendation: "Rename to 'Total Carbohydrates (g)' and add %PDRI.",
      citation: ao('§VI.A.11.a & e'),
    },
    {
      name: 'Protein declaration and %PDRI math',
      status: 'Fail',
      issue: 'Protein declared as 8.1g, not rounded to nearest gram; %PDRI missing (would be ~11%).',
      recommendation: 'Round to 8g and add %PDRI (11%).',
      citation: ao('§VI.A.11.c.1 & d'),
    },
    {
      name: 'Presence + format — Calcium',
      status: 'Fail',
      issue: 'Calcium is declared (198mg/serve, 659mg/100g); only gap is missing %PDRI.',
      recommendation: 'Add %PDRI for calcium.',
      citation: ao('§VI.A.11.a'),
    },
    {
      name: 'Presence + format — sugars',
      status: 'Warning',
      issue: "Nutrient declared as 'sugars' (plural) with value 'Less than 1.0g' — not an integer.",
      recommendation: "Change to 'Sugar' (singular) and round to the nearest gram or '0g'.",
      citation: ao('§VI.A.11.a'),
    },
    {
      name: 'Presence + format — Saturated fat',
      status: 'Pass',
      confirmation: 'Declared correctly, no compliance issue identified.',
      citation: ao('§VI.A.11.a'),
    },
    {
      name: 'Presence + format — Sodium',
      status: 'Pass',
      confirmation: 'Declared correctly, no compliance issue identified.',
      citation: ao('§VI.A.11.a'),
    },
    {
      name: 'Net weight ↔ servings consistency',
      status: 'Pass',
      confirmation: 'Net weight and servings-per-package figures are mathematically consistent.',
      citation: ao('§VI.A.4 & 11.b'),
    },
    {
      name: 'Atwater verification',
      status: 'Pass',
      confirmation: 'Declared energy value is consistent with the Atwater-factor recalculation from macronutrients.',
      citation: ao('§VI.A.11.g'),
    },
  ],
}

export const compositionSection: PhSection = {
  title: 'Composition',
  checks: '8 checks',
  chips: [],
  findings: [
    {
      name: 'Additive declaration format',
      status: 'Inconclusive',
      issue:
        "AO 2014-0030 requires common name + functional category for additive declarations, not explicitly an INS number. 'Enzyme (Non-Animal Rennet)' likely already satisfies this; processing-aid enzymes like rennet typically carry no assigned Codex INS number.",
      recommendation: 'Confirm with FDA/regulatory counsel whether an INS number is expected in practice before adding one.',
      citation: ao('§VI.A.3.k'),
    },
    {
      name: 'Ingredient Permissibility — Enzyme',
      status: 'Skip',
      confirmation:
        'Covered by the additive declaration finding above and the Enzyme permissibility pass elsewhere — no separate action needed.',
      citation: { text: 'BC 2006-016', url: BC_URL },
    },
    {
      name: 'Ingredient Permissibility — Salt',
      status: 'Pass',
      confirmation: "Basic food ingredient, not a food additive — outside this circular's scope.",
      citation: { text: 'BC 2006-016 (scope — additives only)', url: BC_URL },
    },
    {
      name: 'Ingredient Permissibility — Cheese',
      status: 'Pass',
      confirmation: "Basic food ingredient, not a food additive — outside this circular's scope.",
      citation: { text: 'BC 2006-016 (scope — additives only)', url: BC_URL },
    },
    {
      name: 'Compound parens (compound ingredient declaration)',
      status: 'Pass',
      confirmation:
        "Nested compound-ingredient format (e.g. 'Brown Rice Crackers (21%) (Brown Rice (48%)…)') is correctly formed.",
      citation: booklet('p.62'),
    },
    {
      name: 'Ingredient list heading',
      status: 'Pass',
      confirmation: 'Heading wording is correctly formatted.',
      citation: booklet('p.62'),
    },
    {
      name: 'Ingredient ordering',
      status: 'Pass',
      confirmation: 'Ingredients are listed in correct descending-weight order.',
      citation: booklet('p.62'),
    },
    {
      name: 'Quantitative ingredient declaration',
      status: 'Pass',
      confirmation: 'Percentages are declared for all characterising ingredients.',
      citation: ao('§VI.A.3.e'),
    },
  ],
}

export const allergensSection: PhSection = {
  title: 'Allergens',
  checks: '1 check',
  chips: [],
  findings: [
    {
      name: 'Allergen spatial adjacency',
      status: 'Pass',
      confirmation: 'Allergen statement sits in correct proximity to the ingredient list.',
      citation: ao('§VI.A.9'),
    },
  ],
}

export const mandatoryElementsSection: PhSection = {
  title: 'Mandatory Elements',
  checks: '11 checks',
  chips: [],
  findings: [
    {
      name: 'Nutrition Information (mandatory statement)',
      status: 'Fail',
      issue: "The mandatory statement 'Based on PDRI 2015' is missing from the panel.",
      recommendation: "Add 'Based on PDRI 2015' below the Nutrition Information table.",
      citation: circular('§IV.B'),
    },
    {
      name: 'FAN (LTO + FR number)',
      status: 'Warning',
      issue:
        "FAN not shown on label. AO 2014-0030 uses permissive language ('may be printed'), not a strict mandate — but many PH retailers require it commercially, and the company must hold a valid LTO/FR/CPR regardless.",
      recommendation: 'Add LTO/FR numbers for retailer readiness.',
      citation: ao('§VI.B.4'),
    },
    {
      name: 'Country of Origin',
      status: 'Skip',
      confirmation: "Correctly skipped — covered by the 'MADE IN NEW ZEALAND' claim pass in Claims.",
      citation: ao('§VI.A.5'),
    },
    {
      name: 'Date Marking',
      status: 'Skip',
      confirmation: 'Correctly skipped — no separate action needed.',
      citation: ao('§VI.A.8'),
    },
    {
      name: 'Lot Identification',
      status: 'Skip',
      confirmation: 'Correctly skipped — no separate action needed.',
      citation: ao('§VI.A.6'),
    },
    {
      name: 'Allergen Information',
      status: 'Pass',
      confirmation: 'Allergen statement present and correctly worded.',
      citation: ao('§VI.A.9'),
    },
    { name: 'Brand Name', status: 'Pass', confirmation: 'Brand name present and conspicuous.', citation: ao('§VI.A.2') },
    { name: 'Ingredient List', status: 'Pass', confirmation: 'Ingredient list present and correctly formatted.', citation: ao('§VI.A.3') },
    { name: 'Name and Address', status: 'Pass', confirmation: 'Manufacturer name and address declared.', citation: ao('§VI.A.5') },
    { name: 'Net Content', status: 'Pass', confirmation: 'Net content correctly declared.', citation: ao('§VI.A.4') },
    { name: 'Product Name', status: 'Pass', confirmation: 'Product name present and correctly formatted.', citation: ao('§VI.A.1') },
  ],
}

export const claimsSection: PhSection = {
  title: 'Claims',
  checks: '9 checks',
  chips: [],
  findings: [
    {
      name: 'Nutrient comparative claim threshold — 27% MORE PROTEIN',
      status: 'Fail',
      issue:
        'Absolute difference between the product (26.9g/100g) and the reference (~21.3g/100g) is under 6g/100g, short of the required 7.1g/100g minimum — even though the 27% relative figure clears the 25% relative-difference threshold.',
      recommendation: 'Remove the comparative claim, or substantiate a larger absolute protein difference.',
      citation: codex('§6.3'),
    },
    {
      name: 'Religious certification claim — HALAL',
      status: 'Fail',
      issue: 'Generic halal seal graphic with no accredited certifying-body name, logo, or certificate number.',
      recommendation: "Add a recognized halal certifying body's logo/certificate number, or remove the claim.",
      citation: { text: 'RA 10817', url: 'https://lawphil.net/statutes/repacts/ra2016/ra_10817_2016.html' },
    },
    {
      name: 'Nutrient comparative claim — 57% LESS FAT',
      status: 'Warning',
      issue:
        'The claim already carries a linking asterisk to its back-panel footnote — no missing symbol. Open question: the reference-food identity should appear in close proximity to the claim, and the footnote sits on a different panel.',
      recommendation: "Confirm with regulatory counsel whether current placement satisfies 'close proximity.'",
      citation: codex('§6.2'),
    },
    {
      name: 'SOURCE OF CALCIUM',
      status: 'Pass',
      confirmation: 'Clears the 15% NRV threshold several times over.',
      citation: codex(),
    },
    {
      name: '8g PROTEIN PER SERVE',
      status: 'Pass',
      confirmation: "26.9g/100g clears even the Codex 'high' protein threshold.",
      citation: codex(),
    },
    {
      name: 'MADE IN NEW ZEALAND',
      status: 'Pass',
      confirmation: 'Consistent with back-panel sourcing statement.',
      citation: ao('§VI.A.5.e'),
    },
    {
      name: 'VEGETARIAN FRIENDLY',
      status: 'Pass',
      confirmation: 'No meat ingredients; rennet is non-animal.',
      citation: { text: 'RA 3720 §11', url: 'https://elibrary.judiciary.gov.ph/thebookshelf/showdocs/2/24284' },
    },
    {
      name: 'Packed with protein to help keep you going for longer',
      status: 'Pass',
      confirmation: 'General marketing phrasing, not misleading given genuinely high protein content.',
      citation: {
        text: 'FDA Draft Circular — Acceptable Nutrition/Health Claims, Annex A',
        url: 'https://www.fda.gov.ph/wp-content/uploads/2024/07/Draft-FDA-Circular_-Guidelines-on-the-Use-of-Acceptable-Nutrition-and-Health-Claims.pdf',
      },
    },
    {
      name: 'NATURAL CHEESE',
      status: 'Pass',
      confirmation: 'Consistent with genuinely natural, unflavoured cheese ingredient.',
      citation: ao('§V.A & VII'),
    },
  ],
}

export const visualLayoutSection: PhSection = {
  title: 'Visual & Layout Review',
  checks: '3 checks',
  chips: ['3 NEEDS MANUAL REVIEW'],
  findings: [
    {
      name: 'Minimum text height — mandatory label information',
      status: 'Review',
      explanation:
        "PH regulation requires mandatory label information to meet a minimum text height, but exact point size can't be reliably measured from a flattened artwork file. Verify against the vector source file or a printed proof.",
      checklist: [
        'Net content declaration meets minimum height',
        'Nutrition Information panel meets minimum height',
        'Allergen statement meets minimum height',
      ],
      citation: ao('§V.D'),
    },
    {
      name: 'Footnote symbol and proximity — comparative claims',
      status: 'Review',
      explanation:
        "The asterisk after '57% LESS FAT*' and '27% MORE PROTEIN*' correctly links to the back-panel footnote. Whether the footnote sits in reasonably close proximity is a judgment call the engine flags but doesn't resolve.",
      checklist: [
        'Asterisk appears immediately after each claim (confirmed)',
        'Footnote text is easy to locate despite being on a different panel',
        'Same review applies to both comparative claims',
      ],
      citation: codex('§6.2'),
    },
    {
      name: 'Bold formatting — allergen statement',
      status: 'Review',
      explanation:
        "PH regulation expects the CONTAINS/MAY CONTAIN statement to be clearly distinguished, commonly via bold type — font weight is unreliable to verify from this artwork's resolution.",
      checklist: [
        'CONTAINS: Milk, Sesame is bold in the source file',
        'MAY CONTAIN: Wheat, Gluten is bold or otherwise distinguished',
        'Statement remains legible at the printed 30g sachet size',
      ],
      citation: ao('§VI.A.9'),
    },
  ],
}

const permitted = 'Standard, permitted ingredient — no restriction applies.'

export const ingredientPermissibilitySection: PhSection = {
  title: 'Ingredient Permissibility',
  checks: '9 checks',
  chips: [],
  findings: [
    { name: 'Flax Seeds', status: 'Pass', confirmation: permitted, citation: booklet('p.99') },
    { name: 'Brown Rice Crackers', status: 'Pass', confirmation: permitted, citation: booklet('p.62') },
    { name: 'Potato Starch', status: 'Pass', confirmation: permitted, citation: ao('Annex A') },
    { name: 'Pasteurised Milk', status: 'Pass', confirmation: permitted, citation: booklet('p.108') },
    { name: 'Brown Rice', status: 'Pass', confirmation: permitted, citation: { text: 'RA 8976', url: RA_8976_URL } },
    { name: 'Cultures', status: 'Pass', confirmation: permitted, citation: ao(', general ingredient rules') },
    { name: 'Rice', status: 'Pass', confirmation: permitted, citation: { text: 'RA 8976', url: RA_8976_URL } },
    { name: 'Black Sesame Seeds', status: 'Pass', confirmation: permitted, citation: booklet('p.99') },
    { name: 'Sunflower Oil', status: 'Pass', confirmation: permitted, citation: ao('Annex A, p.63') },
  ],
}

export const additivesSection: PhSection = {
  title: 'Additives',
  checks: '1 check',
  chips: [],
  findings: [
    {
      name: 'Permissibility — Enzyme (Non-Animal Rennet)',
      status: 'Pass',
      confirmation: 'Standard, approved processing aid.',
      citation: { text: 'BC 2006-016, p.225', url: BC_URL },
    },
  ],
}

export const illustrationsSection: PhSection = {
  title: 'Illustrations',
  checks: '1 check',
  chips: [],
  findings: [
    {
      name: 'Serving suggestion disclaimer (pictorial misbranding check)',
      status: 'Pass',
      confirmation: 'No misleading pictorial elements identified.',
      citation: ao('§VII.N.3'),
    },
  ],
}

export const phLabelSections: PhSection[] = [
  nutritionPanelSection,
  compositionSection,
  allergensSection,
  mandatoryElementsSection,
  claimsSection,
  visualLayoutSection,
  ingredientPermissibilitySection,
  additivesSection,
  illustrationsSection,
]
